import { useEffect } from "react";

import { BUILD_VERSION } from "@/lib/build-version";

const ENDPOINT = "/api/public/version";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_KEY = "sunset:build-reloaded";

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${ENDPOINT}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "cache-control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data.version === "string" ? data.version : null;
  } catch {
    return null;
  }
}

/**
 * Détecte automatiquement un nouveau déploiement et recharge la page une
 * seule fois par version, afin que le navigateur ne serve jamais un ancien
 * chunk JS/CSS mis en cache.
 */
export function useBuildVersionCheck() {
  useEffect(() => {
    if (typeof window === "undefined" || BUILD_VERSION === "dev") return;

    let cancelled = false;

    const check = async () => {
      if (cancelled || document.visibilityState === "hidden") return;
      const deployed = await fetchDeployedVersion();
      if (cancelled || !deployed || deployed === BUILD_VERSION) return;

      // Une seule rechargement par version déployée (anti-boucle).
      if (sessionStorage.getItem(RELOAD_KEY) === deployed) return;
      sessionStorage.setItem(RELOAD_KEY, deployed);
      window.location.reload();
    };

    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    void check();

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
